import React, { useState, useEffect, useMemo } from 'react';
import MaterialTable from 'material-table';
import { useSelector, useDispatch } from 'react-redux';
import CircularLoading from '../components/CircularLoading';
import { api } from 'common';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import LocationCityOutlinedIcon from '@mui/icons-material/LocationCityOutlined';
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined';
import StarsOutlinedIcon from '@mui/icons-material/StarsOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import BlankTable from '../components/Table/BlankTable';
import localization from '../components/Table/Localization';
import {
  C2,
  C2_FONT,
  c2TableTheme,
  c2TableOptions,
  c2TableStyle,
  c2Toolbar,
  c2Localization,
  C2TableCard,
  C2AddButton,
  C2EmptyState,
  C2Switch,
  C2Select,
  C2SummaryCard as SummaryCard,
  C2ProgressBar,
  C2StatusChip,
} from '../components/Table/C2UserTable';

const DEFAULT_SLOTS = 100;

const slotsOf = (row) => {
  const used = Number(row.slotsUsed) || 0;
  const total = Number(row.slotsTotal) || DEFAULT_SLOTS;
  return { used, total, full: !!row.slotsFull || used >= total };
};

export default function Zonificacion() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { editZonificacionCity, fetchZonificacionCities } = api;
  const settings = useSelector((state) => state.settingsdata.settings);
  const zonificacion = useSelector((state) => state.zonificacion);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [phaseFilter, setPhaseFilter] = useState('');

  useEffect(() => {
    dispatch(fetchZonificacionCities());
  }, [dispatch, fetchZonificacionCities]);

  useEffect(() => {
    setData(zonificacion.cities || []);
  }, [zonificacion.cities]);

  const summary = useMemo(() => {
    let active = 0;
    let special = 0;
    let used = 0;
    let total = 0;
    (data || []).forEach((row) => {
      const s = slotsOf(row);
      if (row.active) active += 1;
      if (!s.full) special += 1;
      used += s.used;
      total += s.total;
    });
    return { cities: data.length, active, special, open: data.length - special, used, total };
  }, [data]);

  const filteredData = useMemo(() => {
    if (!phaseFilter) return data;
    return data.filter((row) => {
      const s = slotsOf(row);
      if (phaseFilter === 'special') return !s.full;
      if (phaseFilter === 'open') return s.full;
      if (phaseFilter === 'inactive') return !row.active;
      return true;
    });
  }, [data, phaseFilter]);

  const columns = [
    {
      title: 'Ciudad',
      field: 'name',
      render: (row) => (
        <Box sx={{ textAlign: 'left' }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.9rem', color: C2.sidebar }}>
            {row.name || '—'}
          </Typography>
          {row.department ? (
            <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.76rem', color: C2.muted }}>{row.department}</Typography>
          ) : null}
        </Box>
      ),
    },
    { title: 'Departamento', field: 'department', hidden: true, searchable: true },
    {
      title: 'Cupos',
      field: 'slotsUsed',
      render: (row) => {
        const s = slotsOf(row);
        return <C2ProgressBar used={s.used} total={s.total} done={s.full} />;
      },
    },
    {
      title: 'Fase',
      field: 'slotsFull',
      render: (row) =>
        slotsOf(row).full ? (
          <C2StatusChip tone="success" label="Abierta (código normal)" />
        ) : (
          <C2StatusChip tone="violet" label="Cupo especial" />
        ),
    },
    {
      title: 'Código maestro',
      field: 'masterCode',
      render: (row) =>
        row.masterCode ? (
          <Box
            component="span"
            sx={{
              px: 1,
              py: 0.4,
              borderRadius: '8px',
              backgroundColor: '#FFFFFF',
              border: `1px dashed ${C2.border}`,
              fontFamily: "'JetBrains Mono', 'Consolas', monospace",
              fontSize: '0.8rem',
              fontWeight: 700,
              color: C2.deep,
              letterSpacing: '0.04em',
            }}
          >
            {row.masterCode}
          </Box>
        ) : (
          '—'
        ),
    },
    {
      title: 'Activa',
      field: 'active',
      render: (rowData) => (
        <C2Switch
          checked={!!rowData.active}
          disabled={!settings?.AllowCriticalEditsAdmin}
          onChange={() => {
            if (!settings?.AllowCriticalEditsAdmin) {
              alert(t('demo_mode'));
              return;
            }
            dispatch(
              editZonificacionCity(
                { ...rowData, active: !rowData.active },
                'Update'
              )
            );
          }}
        />
      ),
    },
  ];

  const tableComponents = c2Toolbar(
    <C2Select
      id="zonificacion-phase-filter"
      label="Filtrar"
      value={phaseFilter}
      onChange={(e) => setPhaseFilter(e.target.value)}
      minWidth={190}
      options={[
        { value: '', label: 'Todas las ciudades' },
        { value: 'special', label: 'Cupo especial' },
        { value: 'open', label: 'Abiertas' },
        { value: 'inactive', label: 'Inactivas' },
      ]}
    />,
    <C2AddButton label="Nueva ciudad" onClick={() => navigate('/zonificacion/edit')} />
  );
  const tableLocalization = c2Localization(
    localization(t),
    <C2EmptyState
      icon={MapOutlinedIcon}
      title={phaseFilter ? 'Sin ciudades para este filtro' : 'Aún no hay ciudades'}
      message={
        phaseFilter
          ? 'Prueba con otro filtro para ver más ciudades.'
          : 'Agrega la primera ciudad para habilitar el registro con cupos especiales en Conecta2.'
      }
    />
  );

  return zonificacion.loading && !data.length ? (
    <CircularLoading />
  ) : (
    <ThemeProvider theme={c2TableTheme}>
      {data.length > 0 ? (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} lg={3}>
            <SummaryCard icon={LocationCityOutlinedIcon} label="Ciudades" value={summary.cities} />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <SummaryCard
              icon={ToggleOnOutlinedIcon}
              label="Activas"
              value={summary.active}
              caption={`${summary.cities - summary.active} inactivas`}
              accent="#16A34A"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <SummaryCard
              icon={StarsOutlinedIcon}
              label="En cupo especial"
              value={summary.special}
              caption={`${summary.open} abiertas con código normal`}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <SummaryCard
              icon={GroupsOutlinedIcon}
              label="Cupos usados"
              value={`${summary.used} / ${summary.total}`}
              caption={summary.total ? `${Math.round((summary.used / summary.total) * 100)}% del total` : null}
            />
          </Grid>
        </Grid>
      ) : null}
      <C2TableCard>
        {data?.length > 0 ? (
          <MaterialTable
            title="Zonificación — Colombia"
            columns={columns}
            style={c2TableStyle(isRTL)}
            data={filteredData}
            components={tableComponents}
            onRowClick={(evt, selectedRow) =>
              setSelectedRow(selectedRow.tableData.id)
            }
            options={c2TableOptions(selectedRow)}
            localization={tableLocalization}
            actions={[
              (rowData) => ({
                icon: () => <EditIcon />,
                tooltip: t('edit'),
                onClick: () => navigate(`/zonificacion/edit/${rowData.id}`),
              }),
            ]}
            editable={{
              onRowDelete: (oldData) =>
                settings?.AllowCriticalEditsAdmin
                  ? new Promise((resolve) => {
                      setTimeout(() => {
                        resolve();
                        dispatch(editZonificacionCity(oldData, 'Delete'));
                      }, 400);
                    })
                  : new Promise((resolve) => {
                      setTimeout(() => {
                        resolve();
                        alert(t('demo_mode'));
                      }, 400);
                    }),
            }}
          />
        ) : (
          <BlankTable
            title="Zonificación — Colombia"
            columns={columns}
            data={[]}
            actions={[]}
            components={tableComponents}
            style={c2TableStyle(isRTL)}
            options={c2TableOptions(null)}
            localization={tableLocalization}
          />
        )}
      </C2TableCard>
    </ThemeProvider>
  );
}
